<template>
  <RevCard>
    <EmptyState
      v-if="!hasData && !pending"
      class="p-24"
      :description="i18n(translations.noDataDescription)"
      :surtitle="i18n(translations.noDataSurtitle)"
      :title="i18n(translations.noDataTitle)"
    />
    <WithSkeletonTableLoader :columns :is-loading="pending">
      <PaginatedTable
        v-if="hasData"
        :columns
        :current-page
        :data="data as GetPastTradeInInvoices"
        :page-size="PAGE_SIZE"
        @page-change="handlePaginationClick"
      >
        <template #body-invoiceNumberHeader="{ item }">
          {{ item.id }}
        </template>
        <template #body-amountOrdersHeader="{ item }">
          {{ i18n.price(item.amountOrders) }}
        </template>
        <template #body-totalHeader="{ item }">
          {{ i18n.price(item.totalTtc) }}
        </template>
        <template #body-endDateHeader="{ item }">
          <span data-allow-mismatch>
            {{
              item.endDate
                ? i18n.date(new Date(item.endDate), FULL_DATE_FORMAT)
                : '-'
            }}
          </span>
        </template>
        <template #body-downloadHeader="{ item }">
          <div class="space-x-16">
            <RevLink v-if="item.billPdfLink" external :to="item.billPdfLink">
              {{ i18n(translations.downloadPdfLink) }}
            </RevLink>
          </div>
        </template>
      </PaginatedTable>
    </WithSkeletonTableLoader>
  </RevCard>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage, navigateTo, useRoute } from '#imports'
import { computed } from 'vue'

import { merchantsAPI } from '@backmarket/http-api'
import type { GetPastTradeInInvoices } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants'
import { useHttpLazyFetch } from '@backmarket/nuxt-module-http/useHttpLazyFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevCard } from '@ds/components/Card'
import { RevLink } from '@ds/components/Link'

import {
  DEFAULT_PAGE,
  FULL_DATE_FORMAT,
  PAGE_SIZE,
} from '../../../../Invoices.constants'
import EmptyState from '../../../../components/EmptyState/EmptyState.vue'
import WithSkeletonTableLoader from '../../../../components/WithSkeletonTableLoader/WithSkeletonTableLoader.vue'
import translations from '../../PastInvoices.translations'

const i18n = useI18n()
const route = useRoute()
const { openErrorToast } = useTheToast()

const currentPage = computed(
  () => parseInt(route.query.page?.toString() || '', 10) || DEFAULT_PAGE,
)

const keys = [
  'invoiceNumberHeader',
  'amountOrdersHeader',
  'totalHeader',
  'endDateHeader',
  'downloadHeader',
] as (keyof typeof translations)[]

const columns = keys.map((key) => ({ key, label: i18n(translations[key]) }))

const { data, error, pending } = await useHttpLazyFetch(
  merchantsAPI.getPastTradeInInvoices,
  {
    queryParams: { page: currentPage },
    watch: [currentPage],
  },
)

if (error.value) {
  openErrorToast({
    content: getApiErrorMessage(error.value as unknown as Error),
  })
}

const hasData = computed(() => !!data.value?.results?.length)

async function handlePaginationClick({ page }: { page: number }) {
  navigateTo({ ...route, query: { page } })
}
</script>
